<template>
  <div class="holiday-builder" :class="{ 'fairness-only': fairnessExpanded }">
    <header class="banner">
      <div class="banner-titles">
        <h2>Holiday Schedule</h2>
        <p>Configure how the children spend each holiday, special day, and school break. The agreement on the right updates as you go.</p>
      </div>
      <div class="banner-actions">
        <button v-if="isPrimary" type="button" class="btn-secondary" @click="resetAll" title="Clear all rules">Reset</button>
        <button type="button" class="btn-primary" @click="printAgreement">↓ Download PDF</button>
      </div>
    </header>

    <div v-show="!fairnessExpanded" class="layout">
      <!-- Left: editor -->
      <section class="editor">
        <!-- Toolbar: overall progress + discuss filter. -->
        <div class="editor-toolbar">
          <div class="overall-progress">
            <strong>{{ decidedCount }} of {{ totalCount }}</strong> decisions made
            <div class="overall-bar">
              <div class="overall-bar-fill" :style="{ width: progressPct + '%' }"></div>
            </div>
          </div>
          <button
            v-if="flaggedCount > 0"
            type="button"
            class="discuss-filter"
            :class="{ active: showOnlyFlagged }"
            :title="showOnlyFlagged ? 'Show all rows' : 'Show only flagged rows'"
            @click="showOnlyFlagged = !showOnlyFlagged"
          >
            <span class="discuss-flag" aria-hidden="true">⚑</span>
            {{ flaggedCount }} to discuss
            <span class="discuss-action">{{ showOnlyFlagged ? '· Show all' : '· Show only' }}</span>
          </button>
        </div>

        <div v-for="cat in groupedHolidays" :key="cat.key" class="category" :class="{ collapsed: !isCategoryOpen(cat.key) }" :data-category-key="cat.key">
          <button
            type="button"
            class="category-head"
            :aria-expanded="isCategoryOpen(cat.key)"
            @click="toggleCategory(cat.key)"
          >
            <span class="cat-chevron" aria-hidden="true">▾</span>
            <span class="cat-label">{{ cat.label }}</span>
            <span class="cat-progress" :class="{ done: cat.decided === cat.list.length && cat.flagged === 0 }">
              {{ cat.decided }} of {{ cat.list.length }} decided<template v-if="cat.flagged > 0"> · {{ cat.flagged }} to discuss</template>
            </span>
          </button>

          <div v-show="isCategoryOpen(cat.key)" class="category-body">
            <details v-if="isPrimary && templatesFor(cat.key).length" class="cat-templates">
              <summary>
                <span class="cat-templates-icon" aria-hidden="true">⚡</span>
                Quick-start templates for {{ cat.label.toLowerCase() }}
              </summary>
              <TemplatePicker
                :templates="templatesFor(cat.key)"
                @apply="(payload) => applyTemplate(cat.key, payload.key, { reverse: payload.reverse })"
              />
            </details>

            <HolidayRow
              v-for="h in cat.list"
              :key="h.id"
              :holiday="h"
              :partyAName="partyAName"
              :partyBName="partyBName"
              :role="role"
              :currentUserName="currentUserName"
              :open="openRows.indexOf(h.id) !== -1"
              @update="updateHoliday"
              @remove="removeHoliday"
              @toggle="toggleRow"
              @apply-to-category="applyRowToCategory"
            />

            <!-- Add custom item for this category (primary only) -->
            <template v-if="isPrimary">
              <div v-if="addingCategory === cat.key" class="add-custom-form">
                <input
                  :ref="`add-${cat.key}`"
                  v-model="newName"
                  type="text"
                  :placeholder="addPlaceholder(cat.key)"
                  maxlength="60"
                  @keyup.enter="confirmAddCustom(cat.key)"
                  @keyup.escape="cancelAdd"
                />
                <button type="button" class="btn-add" @click="confirmAddCustom(cat.key)">Add</button>
                <button type="button" class="btn-cancel" @click="cancelAdd">Cancel</button>
              </div>
              <button v-else type="button" class="add-custom-btn" @click="startAdd(cat.key)">
                + Add {{ addLabel(cat.key) }}
              </button>
            </template>
          </div>
        </div>
      </section>

      <!-- Right: tabbed live preview. Whatever tab is active is what prints. -->
      <aside class="preview">
        <nav class="preview-tabs">
          <button
            v-for="t in TABS"
            :key="t.value"
            type="button"
            :class="['tab', { active: previewTab === t.value }]"
            @click="previewTab = t.value"
          >{{ t.label }}</button>
          <select
            v-if="previewTab === 'year'"
            class="year-select"
            :value="selectedYear"
            @change="selectedYear = Number($event.target.value)"
          >
            <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
          </select>
        </nav>

        <!-- All three preview panes always live in the DOM. On screen, CSS
             shows only the one matching `previewTab`; when printing, CSS
             reveals all three with page breaks between so the PDF contains
             every view in one file. -->
        <div class="preview-stack" :class="`active-${previewTab}`">
          <AgreementPreview
            class="preview-pane preview-agreement"
            :holidays="holidays"
            :partyAName="partyAName"
            :partyBName="partyBName"
            :jurisdiction="jurisdiction"
            :children="children"
          />
          <AgreementTable
            class="preview-pane preview-overall"
            :holidays="holidays"
            :partyAName="partyAName"
            :partyBName="partyBName"
            :jurisdiction="jurisdiction"
            :children="children"
          />
          <YearTable
            class="preview-pane preview-year"
            :holidays="holidays"
            :partyAName="partyAName"
            :partyBName="partyBName"
            :jurisdiction="jurisdiction"
            :children="children"
            :year="selectedYear"
          />
        </div>
      </aside>
    </div>

    <section class="rotation-block">
      <FairnessDashboard
        :holidays="holidays"
        :partyAName="partyAName"
        :partyBName="partyBName"
        :children="children"
        :fill-available="fairnessExpanded"
        @toggle-open="onFairnessToggle"
      />
    </section>

    <!-- Print-only worksheet (PensionCalculator pattern): hidden on screen, all three
         preview panes always rendered so the PDF includes every view. -->
    <div class="print-worksheet" aria-hidden="true">
      <div class="preview-stack">
        <AgreementPreview
          class="preview-pane preview-agreement"
          :holidays="holidays"
          :partyAName="partyAName"
          :partyBName="partyBName"
          :jurisdiction="jurisdiction"
          :children="children"
        />
        <AgreementTable
          class="preview-pane preview-overall"
          :holidays="holidays"
          :partyAName="partyAName"
          :partyBName="partyBName"
          :jurisdiction="jurisdiction"
          :children="children"
        />
        <YearTable
          class="preview-pane preview-year"
          :holidays="holidays"
          :partyAName="partyAName"
          :partyBName="partyBName"
          :jurisdiction="jurisdiction"
          :children="children"
          :year="selectedYear"
        />
      </div>
    </div>
  </div>
</template>

<script>
import HolidayRow from './components/HolidayRow.vue'
import AgreementPreview from './components/AgreementPreview.vue'
import AgreementTable from './components/AgreementTable.vue'
import YearTable from './components/YearTable.vue'
import FairnessDashboard from './components/FairnessDashboard.vue'
import TemplatePicker from './components/TemplatePicker.vue'
import { CATEGORIES } from './seed/holidays.js'
import { load, save, clear, flushSave } from './store.js'
import {
  getScrollContainer,
  readScrollTop,
  writeScrollTop,
  compensateScrollForCollapseAbove,
  measureCollapseAbove,
} from './scrollContainer.js'

const CATEGORY_ORDER = ['holiday', 'special-day', 'school-break', 'christmas']

const TABS = [
  { value: 'agreement', label: 'Agreement' },
  { value: 'overall',   label: 'At a glance' },
  { value: 'year',      label: 'Year view' },
]

// Template application logic. Each applier receives (holiday, ctx) and returns
// the next holiday state. ctx carries party metadata used by gender-aware rules.
// Chronological order of BC statutory holidays. Used by the "alt-stat"
// template so the holidays alternate parent-by-parent across the calendar
// AND each one flips parents the following year, balancing the load on
// both axes (within-year and year-over-year).
// Chronological order slots for the alt-stat / alt-long-weekends templates.
// Province-unique holidays that occupy the same seasonal slot share the same
// index so alternation parity stays consistent regardless of jurisdiction.
// Easter Monday shares Good Friday's slot (Easter weekend cohesion — same
// parent for the whole weekend, not alternating within it).
const STAT_CHRONO_ORDER = {
  // Slot 0 — February / early March (mid-winter observance)
  'family-day':                     0,  // BC, AB, NB, ON, SK, YT
  'louis-riel-day':                 0,  // MB
  'islander-day':                   0,  // PE
  'heritage-day-ns':                0,  // NS
  'st-patricks-day':                0,  // NL (nearest Mon to Mar 17)
  // Slot 1 — Easter weekend + April
  'good-friday':                    1,
  'easter-monday':                  1,
  'st-georges-day':                 1,  // NL (nearest Mon to Apr 23)
  // Slot 2 — Late May & June
  'victoria-day':                   2,
  'national-indigenous-peoples-day': 2, // NT, YT (Jun 21)
  'discovery-day-nl':               2,  // NL (nearest Mon to Jun 24)
  // Slot 3 — July
  'canada-day':                     3,
  'memorial-day':                   3,  // NL (Jul 1, alongside Canada Day)
  'nunavut-day':                    3,  // NU (Jul 9)
  'orangemans-day':                 3,  // NL (nearest Mon to Jul 12)
  // Slot 4 — August (1st Mon usually; PEI Gold Cup is 3rd Fri; YT Discovery is 3rd Mon)
  'bc-day':                         4,  // BC
  'heritage-day':                   4,  // AB (Aug — distinct from NS's Feb Heritage Day)
  'terry-fox-day':                  4,  // MB
  'new-brunswick-day':              4,  // NB
  'saskatchewan-day':               4,  // SK
  'civic-holiday':                  4,  // ON, NS, NT, NU
  'gold-cup-parade-day':            4,  // PE (3rd Fri of August)
  'discovery-day':                  4,  // YT (3rd Mon of August — distinct from NL's June Discovery Day)
  // Slot 5 — Labour Day
  'labour-day':                     5,
  // Slot 6 — Sept 30
  'truth-reconciliation':           6,
  // Slot 7 — Thanksgiving
  'thanksgiving':                   7,
  // Slot 8 — Remembrance Day
  'remembrance-day':                8,
}

// Templates that apply universally to any category: bulk-set every row in
// the section to "no-change" (keep regular parenting time) or "suspend-flex"
// (decide each occurrence by agreement). Spread into every category's array
// below so they're always available regardless of section.
const UNIVERSAL_TEMPLATES = [
  {
    key: 'no-change-all',
    title: "Don't change the schedule",
    description: 'Keep the regular parenting schedule on every day in this section.',
    apply: (h) => ({ ...h, rule: { kind: 'no-change' } }),
  },
  {
    key: 'decide-later-all',
    title: 'Take a flexible approach',
    description: 'Pause the regular schedule on every day in this section — the parties decide together with reasonable notice.',
    apply: (h) => ({ ...h, rule: { kind: 'suspend-flex' } }),
  },
]

// Quick-start templates, scoped per category. Each template's `apply(h, ctx)`
// runs only on holidays in its own category — categories don't influence each other.
const TEMPLATES_BY_CATEGORY = {
  'holiday': [
    {
      key: 'alt-stat',
      title: 'Alternate statutory holidays evenly',
      description: "Within each year the holidays alternate parent-by-parent in chronological order. Each holiday then flips parents the next year, so the load stays balanced across both axes.",
      reversible: true,
      apply: (h) => {
        const idx = STAT_CHRONO_ORDER[h.id]
        if (idx === undefined) return h // custom or unrecognized — leave unset
        const evenParent = idx % 2 === 0 ? 'A' : 'B'
        return { ...h, rule: { kind: 'alternating-by-year', evenParent } }
      },
    },
    {
      key: 'tie-long-weekends',
      title: 'Tie long-weekend holidays to the weekend parent',
      description: "Long-weekend-eligible holidays absorb into whoever has the regular weekend. Mid-week holidays stay unset.",
      apply: (h) => h.longWeekendType ? { ...h, rule: { kind: 'tie-to-weekend' } } : h,
    },
    {
      key: 'alt-long-weekends',
      title: 'Alternate the long weekend each year',
      description: "Long-weekend-eligible holidays apply to the full Fri to Sun (or Sat to Mon) block. Within each year the eligible holidays alternate parent-by-parent in chronological order, and each one flips parents the next year. Mid-week holidays stay unset.",
      reversible: true,
      apply: (h) => {
        if (!h.longWeekendType) return h // mid-week, leave unset
        const idx = STAT_CHRONO_ORDER[h.id]
        if (idx === undefined) return h
        const evenParent = idx % 2 === 0 ? 'A' : 'B'
        return { ...h, rule: { kind: 'alternating-by-year', evenParent, scope: 'long-weekend' } }
      },
    },
    ...UNIVERSAL_TEMPLATES,
  ],

  'special-day': [

    {
      key: 'parent-days',
      title: "Each parent's day with that parent",
      description: "Mother's Day with the parent who uses she/her, Father's Day with the parent who uses he/him, each parent's birthday with themselves, children's birthday shared. Days without a clear pronoun match stay unset.",
      apply: (h, ctx) => {
        if (h.id === 'mothers-day')   return matchByPronoun(h, ctx, 'she/her')
        if (h.id === 'fathers-day')   return matchByPronoun(h, ctx, 'he/him')
        if (h.id === 'party-a-birthday')   return { ...h, rule: { kind: 'fixed', parent: 'A' } }
        if (h.id === 'party-b-birthday')   return { ...h, rule: { kind: 'fixed', parent: 'B' } }
        if (h.id === 'childrens-birthday') return { ...h, rule: { kind: 'share' } }
        return h
      },
    },
    {
      key: 'alt-special',
      title: 'Alternate all special days annually',
      description: 'Every special day alternates by year. A simple starting point if no day stands out as fixed.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'alternating-by-year', evenParent: 'A' } }),
    },
    ...UNIVERSAL_TEMPLATES,
  ],

  'school-break': [
    {
      key: 'alt-breaks',
      title: 'Alternate breaks annually',
      description: 'Each break (Spring, Summer, Winter) goes to one parent for the entire break, alternating yearly.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'alternating-by-year', evenParent: 'A' } }),
    },
    {
      key: 'split-each-break',
      title: 'Split each break in half',
      description: 'First half of each break with one parent, second half with the other.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'split-period', firstHalfParent: 'A' } }),
    },
    {
      key: 'week-on-off-breaks',
      title: 'Week on/off rotation through each break',
      description: 'Parents rotate parenting time on a one-week-on / one-week-off basis through each break.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'week-on-off', startParent: 'A' } }),
    },
    ...UNIVERSAL_TEMPLATES,
  ],

  'christmas': [
    {
      key: 'eve-day-split-alt',
      title: "Eve/Day split, alternating each year",
      description: "Christmas Eve/Day and New Year's Eve/Day each split at noon. Each year the parents swap which 24-hour block they get.",
      reversible: true,
      apply: (h) => {
        if (h.id === 'christmas-eve')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'A', splitTime: '12:00' } }
        if (h.id === 'christmas-day')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'B', splitTime: '12:00' } }
        if (h.id === 'new-years-eve')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'A', splitTime: '12:00' } }
        if (h.id === 'new-years-day')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'B', splitTime: '12:00' } }
        return h
      },
    },
    {
      key: 'split-each-at-noon',
      title: 'Split each day at noon',
      description: 'Each of the four days is split at noon, with the morning going to one parent and the afternoon to the other.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'split-day', firstHalfParent: 'A', transitionTime: '12:00' } }),
    },
    {
      key: 'alt-each-day',
      title: 'Alternate each day annually',
      description: 'Christmas Eve, Christmas Day, NY Eve, and NY Day each alternate by year independently.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'alternating-by-year', evenParent: 'A' } }),
    },
    ...UNIVERSAL_TEMPLATES,
  ],
}

function matchByPronoun(h, ctx, target) {
  const aMatches = ctx.partyAPronouns === target
  const bMatches = ctx.partyBPronouns === target
  if (aMatches && !bMatches) return { ...h, rule: { kind: 'fixed', parent: 'A' } }
  if (bMatches && !aMatches) return { ...h, rule: { kind: 'fixed', parent: 'B' } }
  return h // both match (same-gender couple) or neither match — leave unset
}

// Flip every A↔B parent reference in a rule. Used by the per-category
// "Swap parents" button to mirror an arrangement (e.g. start the
// chronological alternation with the other parent).
const SWAP_FIELDS = ['parent', 'evenParent', 'firstHalfParent', 'startParent']
function swapAB(p) {
  if (p === 'A') return 'B'
  if (p === 'B') return 'A'
  return p
}
function swapRule(rule) {
  if (!rule) return rule
  const next = { ...rule }
  for (const field of SWAP_FIELDS) {
    if (next[field] === 'A' || next[field] === 'B') next[field] = swapAB(next[field])
  }
  return next
}

const NEEDS_DECISION_KINDS = new Set(['unset', 'agreement-required'])
function isUnset(h) {
  return NEEDS_DECISION_KINDS.has(h.rule.kind)
}

const ARRANGEMENT_COPY_FIELDS = ['pickup', 'dropoff', 'timesByAgreement', 'customProse', 'notes']

function copyArrangementFields(source) {
  const patch = { rule: { ...source.rule } }
  for (const field of ARRANGEMENT_COPY_FIELDS) {
    patch[field] = source[field]
  }
  return patch
}

export default {
  name: 'HolidayBuilder',
  components: { HolidayRow, AgreementPreview, AgreementTable, YearTable, FairnessDashboard, TemplatePicker },
  props: {
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    partyAPronouns: { type: String, default: '' },
    partyBPronouns: { type: String, default: '' },
    jurisdiction: { type: String, default: 'BC' },
    caseId: { type: String, default: '' },
    children: { type: Array, default: () => [] },
    // Permission role. 'primary' (default) can edit rules, add/remove custom
    // rows, run templates, and reset. 'secondary' is a comment-only role that
    // can toggle the discuss flag and write a discussionNote per holiday but
    // cannot modify the rule, times, or membership of the list. Wire this
    // from Divii's case permissions on a per-user basis.
    role: {
      type: String,
      default: 'primary',
      validator: function (v) { return v === 'primary' || v === 'secondary' },
    },
    // Display name of the user currently signed in on this session. Stamped
    // onto every discussionNote this session writes so the other party can
    // see "Note from Gwyneth" instead of a generic label. Divii should pass
    // its authenticated user's display name (e.g. currentUser.displayName).
    currentUserName: { type: String, default: '' },
  },
  data() {
    return {
      hydrated: false,
      holidays: [],
      previewTab: 'agreement',
      selectedYear: new Date().getFullYear(),
      TABS,
      addingCategory: null,
      newName: '',
      // Accordion + per-row open state. Plain arrays (Vue 2's reactivity
      // doesn't track Set / Map mutations, so Sets won't trigger re-render).
      openCategories: [],
      openRows: [],
      // When true, the editor only renders rows that have flaggedForDiscussion: true.
      // Toggled from the toolbar's "X to discuss · Show only" chip.
      showOnlyFlagged: false,
      // When fairness is expanded, hide the editor+preview split underneath.
      fairnessExpanded: false,
    }
  },
  computed: {
    isPrimary() { return this.role !== 'secondary' },
    isSecondary() { return this.role === 'secondary' },
    anyConfigured() {
      return this.holidays.some((h) => !NEEDS_DECISION_KINDS.has(h.rule.kind))
    },
    groupedHolidays() {
      // When the discuss filter is active, narrow the source list before
      // grouping so categories with zero matches drop out entirely.
      const source = this.showOnlyFlagged
        ? this.holidays.filter((h) => h.flaggedForDiscussion)
        : this.holidays
      const byCategory = {}
      for (const h of source) (byCategory[h.category] ||= []).push(h)
      return CATEGORY_ORDER
        .filter((k) => byCategory[k]?.length)
        .map((k) => {
          const list = byCategory[k]
          const decided = list.filter((h) => !isUnset(h)).length
          const flagged = list.filter((h) => h.flaggedForDiscussion).length
          return { key: k, label: CATEGORIES[k], list, decided, flagged }
        })
    },
    decidedCount() {
      return this.holidays.filter((h) => !isUnset(h)).length
    },
    totalCount() {
      return this.holidays.length
    },
    flaggedCount() {
      return this.holidays.filter((h) => h.flaggedForDiscussion).length
    },
    progressPct() {
      if (this.totalCount === 0) return 0
      return Math.round((this.decidedCount / this.totalCount) * 100)
    },
    availableYears() {
      const start = new Date().getFullYear()
      // Offer current year + next 4
      return [start, start + 1, start + 2, start + 3, start + 4]
    },
  },
  watch: {
    holidays: {
      deep: true,
      handler(v) {
        if (!this.hydrated) return
        save(this.caseId, v)
      },
    },
    // Refresh seeded names if the parent app passes new party names (e.g. user
    // edits them in the wider Divii case). We only rewrite *unmodified* names
    // — the birthday rows that contain the party-name placeholder.
    partyAName() { this.refreshPlaceholderNames() },
    partyBName() { this.refreshPlaceholderNames() },
    // Same idea for the children's-birthday row: when there's a single child
    // with a name we use "{Child}'s Birthday" instead of "Children's Birthday".
    children: {
      deep: true,
      handler() { this.refreshPlaceholderNames() },
    },
    // If the user un-flags the last flagged row while the filter is on, the
    // filter chip disappears but the editor would still be filtered to an
    // empty set. Auto-clear the filter so they see all rows again.
    flaggedCount(n) {
      if (n === 0 && this.showOnlyFlagged) this.showOnlyFlagged = false
    },
    caseId: {
      async handler(nextId, prevId) {
        if (!nextId || nextId === prevId || !this.hydrated) return
        this.hydrated = false
        this.holidays = await load(nextId, this.partyAName, this.partyBName, this.jurisdiction)
        this.refreshPlaceholderNames()
        this.openCategories = []
        this.openRows = []
        this.hydrated = true
      },
    },
  },
  async created() {
    this.holidays = await load(this.caseId, this.partyAName, this.partyBName, this.jurisdiction)
    this.refreshPlaceholderNames()
    this.openCategories = []
    this.openRows = []
    this.hydrated = true
    this._flushHolidaySave = () => flushSave(this.caseId)
    window.addEventListener('pagehide', this._flushHolidaySave)
  },
  async beforeDestroy() {
    window.removeEventListener('pagehide', this._flushHolidaySave)
    await flushSave(this.caseId)
  },
  methods: {
    isCategoryOpen(key) {
      return this.openCategories.indexOf(key) !== -1
    },
    toggleCategory(key) {
      const catEl = this.$el.querySelector(`[data-category-key="${key}"]`)
      const scroller = getScrollContainer(catEl) || document.documentElement
      const wasOpen = this.openCategories.indexOf(key) !== -1
      let collapseDelta = 0

      if (!wasOpen) {
        const prevKey = this.openCategories[0]
        if (prevKey && prevKey !== key) {
          const prevEl = this.$el.querySelector(`[data-category-key="${prevKey}"]`)
          const prevBody = prevEl?.querySelector('.category-body')
          collapseDelta = measureCollapseAbove(prevEl, catEl, prevBody)
        }
      }

      const scrollTop = readScrollTop(scroller)
      if (wasOpen) {
        this.openCategories = []
      } else {
        this.openCategories = [key]
      }

      if (!wasOpen && collapseDelta > 0) {
        this.$nextTick(() => {
          compensateScrollForCollapseAbove(scroller, scrollTop, collapseDelta)
        })
      }
    },
    toggleRow(id) {
      const rowEl = this.$el.querySelector(`[data-holiday-id="${id}"]`)
      const scroller = getScrollContainer(rowEl) || document.documentElement
      const wasOpen = this.openRows.indexOf(id) !== -1
      const anchorEl = rowEl && (rowEl.querySelector('.row-head') || rowEl)
      const anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null
      const anchorScrollTop = readScrollTop(scroller)
      this.openRows = wasOpen ? [] : [id]

      if (!wasOpen && anchorEl && anchorTop != null) {
        const keepAnchor = () => {
          const delta = anchorEl.getBoundingClientRect().top - anchorTop
          if (Math.abs(delta) < 1) return
          writeScrollTop(scroller, Math.max(0, anchorScrollTop + delta))
        }
        this.$nextTick(() => {
          keepAnchor()
          requestAnimationFrame(() => {
            keepAnchor()
            requestAnimationFrame(keepAnchor)
          })
        })
      }
    },
    updateHoliday({ id, patch }) {
      const idx = this.holidays.findIndex((h) => h.id === id)
      if (idx === -1) return
      this.holidays.splice(idx, 1, { ...this.holidays[idx], ...patch })
      if (this.hydrated) save(this.caseId, this.holidays)
    },
    // Copy one row's rule onto every other row in the same category. Confirmed
    // with the user before applying since it overwrites their other choices.
    // Like a quick-start template but carrying a specific configured arrangement.
    applyRowToCategory(sourceId) {
      const source = this.holidays.find((h) => h.id === sourceId)
      if (!source) return
      const cat = source.category
      const others = this.holidays.filter((h) => h.category === cat && h.id !== sourceId)
      if (!others.length) return
      const catLabel = CATEGORIES[cat] || 'this section'
      const msg = `Apply this arrangement to all ${others.length} other ${catLabel}? This will overwrite their current rules.`
      if (!confirm(msg)) return
      this.holidays = this.holidays.map((h) => {
        if (h.category !== cat || h.id === sourceId) return h
        return { ...h, ...copyArrangementFields(source) }
      })
    },
    removeHoliday(id) {
      const h = this.holidays.find((h) => h.id === id)
      if (!h) return
      if (!confirm(`Remove ${h.name}?`)) return
      this.holidays = this.holidays.filter((h) => h.id !== id)
      // Also clean the row out of openRows so it doesn't leak.
      if (this.openRows.indexOf(id) !== -1) {
        this.openRows = this.openRows.filter((rowId) => rowId !== id)
      }
    },
    startAdd(catKey) {
      this.addingCategory = catKey
      this.newName = ''
      this.$nextTick(() => {
        const el = this.$refs[`add-${catKey}`]
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    cancelAdd() {
      this.addingCategory = null
      this.newName = ''
    },
    confirmAddCustom(catKey) {
      const name = this.newName.trim()
      if (!name) return
      const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      this.holidays.push({
        id,
        category: catKey,
        name,
        longWeekendType: null,
        rule: { kind: 'unset' },
        pickup: '',
        dropoff: '',
        timesByAgreement: false,
        customProse: '',
        notes: '',
        whyThisMatters: '',
        flaggedForDiscussion: false,
        discussionNote: null,
        customName: false,
      })
      // New custom rows start with the editor open so the user can
      // configure the rule right away. The category will already be open
      // (you had to expand it to click "+ Add").
      this.openRows = this.openRows.concat(id)
      this.cancelAdd()
    },
    addLabel(catKey) {
      const map = {
        'holiday':       'a holiday',
        'special-day':   'a special day',
        'school-break':  'a school break',
        'christmas':     'another day',
      }
      return map[catKey] || 'item'
    },
    addPlaceholder(catKey) {
      const map = {
        'holiday':       'e.g. Diwali, Eid, National Indigenous Peoples Day',
        'special-day':   'e.g. First day of school, anniversary',
        'school-break':  'e.g. Mid-winter break',
        'christmas':     'e.g. Boxing Day, Hanukkah',
      }
      return map[catKey] || 'Name'
    },
    applyTemplate(categoryKey, templateKey, options = {}) {
      const list = TEMPLATES_BY_CATEGORY[categoryKey] || []
      const t = list.find((x) => x.key === templateKey)
      if (!t) return
      const ctx = {
        partyAPronouns: this.partyAPronouns,
        partyBPronouns: this.partyBPronouns,
      }
      this.holidays = this.holidays.map((h) => {
        if (h.category !== categoryKey) return h
        const next = t.apply(h, ctx)
        // Reverse only flips parent assignments on rules the template just produced.
        // It's a no-op for non-reversible rule shapes (tie-to-weekend, share, etc.).
        return options.reverse ? { ...next, rule: swapRule(next.rule) } : next
      })
    },
    templatesFor(categoryKey) {
      return TEMPLATES_BY_CATEGORY[categoryKey] || []
    },
    async resetAll() {
      if (!confirm('Clear every rule and start over?')) return
      this.hydrated = false
      this.holidays = await clear(this.caseId, this.partyAName, this.partyBName, this.jurisdiction)
      this.openCategories = []
      this.openRows = []
      this.hydrated = true
    },
    printAgreement() {
      const title = `Holiday Schedule - ${this.partyAName || 'Party A'} and ${this.partyBName || 'Party B'}`
      const prevTitle = document.title
      document.title = title
      // Same pattern as PensionCalculator: gate print CSS on a body class and
      // call window.print() — no iframe or stylesheet harvesting (breaks in prod).
      document.body.classList.add('holiday-schedule-printing')
      const cleanup = () => {
        document.title = prevTitle
        document.body.classList.remove('holiday-schedule-printing')
        window.removeEventListener('afterprint', cleanup)
      }
      window.addEventListener('afterprint', cleanup)
      window.print()
    },
    refreshPlaceholderNames() {
      const A = this.partyAName || 'Party A'
      const B = this.partyBName || 'Party B'
      const childBirthdayName = this.computeChildBirthdayName()
      this.holidays = this.holidays.map((h) => {
        // Respect the user's manual rename — once customName is true, we
        // stop auto-substituting party names into this row.
        if (h.customName) return h
        if (h.id === 'party-a-birthday') return { ...h, name: `${A}'s Birthday` }
        if (h.id === 'party-b-birthday') return { ...h, name: `${B}'s Birthday` }
        if (h.id === 'childrens-birthday') return { ...h, name: childBirthdayName }
        return h
      })
    },
    // When there's exactly one child with a name, use "Sasha's Birthday"
    // instead of the generic "Children's Birthday". Falls back to the
    // generic when there are 0 or 2+ children, or when the single child
    // has no name yet.
    computeChildBirthdayName() {
      if (Array.isArray(this.children) && this.children.length === 1) {
        const name = this.children[0]?.name?.trim()
        if (name) return `${name}'s Birthday`
      }
      return "Children's Birthday"
    },
    onFairnessToggle(isOpen) {
      this.fairnessExpanded = !!isOpen
    },
  },
}
</script>

<style scoped>
.holiday-builder {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  flex: 1;
  height: 100%;
  max-height: 100%;
}
.holiday-builder.fairness-only .rotation-block {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* Divii-style dark-teal section banner (matches the Support Calculator pattern) */
.banner {
  background: #34555f;
  color: #fff;
  border-radius: 10px;
  padding: 0.95rem 1.15rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.banner-titles h2 {
  margin: 0 0 0.1rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.banner-titles p {
  margin: 0;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.78);
  max-width: 60ch;
}
.banner-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
.banner-actions .btn-primary,
.banner-actions .btn-secondary {
  font-family: inherit;
  font-size: 0.82rem;
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
}
.banner-actions .btn-primary {
  background: #f7a91b !important;
  color: #1e293b !important;
  border-color: #f7a91b !important;
}
.banner-actions .btn-primary:hover {
  background: #d8911a !important;
  border-color: #d8911a !important;
}
.banner-actions .btn-secondary {
  background: transparent !important;
  color: #fff !important;
  border-color: rgba(255,255,255,0.4) !important;
}
.banner-actions .btn-secondary:hover {
  background: rgba(255,255,255,0.08) !important;
  border-color: rgba(255,255,255,0.6) !important;
}

/* Editor toolbar: overall progress indicator + bulk expand/collapse links. */
.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.65rem 0.9rem;
}
.overall-progress {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.82rem;
  color: #475569;
  flex-grow: 1;
  min-width: 0;
}
.overall-progress strong {
  color: #1e293b;
  font-weight: 600;
}
.overall-bar {
  flex-grow: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  max-width: 200px;
}
.overall-bar-fill {
  height: 100%;
  background: #3f6772;
  transition: width 0.25s ease;
}
/* Discuss filter chip — only appears when at least one row is flagged.
   Default state: amber outline (informational). Active state: solid amber
   (filtering on). Click toggles whether the editor narrows to flagged rows. */
.discuss-filter {
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 500;
  background: #fef6e0;
  color: #85581a;
  border: 1px solid #dc6b1c;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.discuss-filter:hover {
  border-color: #d8911a;
  background: #fde9bf;
}
.discuss-filter.active {
  background: #f7a91b;
  border-color: #d8911a;
  color: #fff;
}
.discuss-filter.active:hover {
  background: #d8911a;
}
.discuss-filter .discuss-flag { font-size: 0.85rem; }
.discuss-filter .discuss-action { color: inherit; opacity: 0.85; }

/* Per-category quick-start templates collapsible (one per category in the
   editor). Treated as a brand-gold call-to-action so it reads as "click
   me to skip ahead" rather than blending into the surrounding category. */
.cat-templates {
  background: #fef6e0;
  border: 1px solid #fde9bf;
  border-left: 3px solid #f7a91b;
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  margin-bottom: 0.6rem;
  transition: background 0.15s, border-color 0.15s;
}
.cat-templates:hover {
  background: #fde9bf;
}
.cat-templates summary {
  cursor: pointer;
  font-size: 0.86rem;
  color: #85581a;
  font-weight: 600;
  user-select: none;
  padding: 0.2rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cat-templates-icon {
  font-size: 1rem;
  line-height: 1;
}
.cat-templates[open] summary {
  margin-bottom: 0.55rem;
  border-bottom: 1px dashed #fde9bf;
  padding-bottom: 0.5rem;
}
.cat-templates[open]:hover {
  background: #fef6e0;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}
@media (max-width: 1000px) {
  .layout { grid-template-columns: 1fr; }
  .holiday-builder {
    height: auto;
    max-height: none;
    flex: none;
  }
  .editor {
    height: auto;
    max-height: calc(100vh - 280px);
  }
  .preview {
    height: auto;
    max-height: calc(100vh - 280px);
    top: 1rem;
  }
}

.editor {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.5rem;
}
.preview {
  position: sticky;
  top: 0;
  align-self: start;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  -webkit-overflow-scrolling: touch;
}

/* On screen: only the active preview pane is visible; the others stay
   mounted in the DOM (so their data is up-to-date) but are display:none.
   On print: all three become visible, with page breaks between so the
   downloaded PDF contains every view in one file. */
.preview-stack .preview-pane { display: none; }
.preview-stack.active-agreement .preview-agreement,
.preview-stack.active-overall .preview-overall,
.preview-stack.active-year .preview-year { display: block; }
@media print {
  .preview-stack .preview-pane { display: block !important; }
  .preview-stack .preview-pane + .preview-pane {
    page-break-before: always;
    break-before: page;
  }
}

/* Tabs in the preview pane */
.preview-tabs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.4rem 0.5rem;
}
.tab {
  font-family: inherit;
  background: transparent;
  border: 1px solid transparent;
  color: #475569;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.78rem;
  cursor: pointer;
  font-weight: 500;
}
.tab:hover { color: #34555f; }
.tab.active {
  background: #3f6772;
  color: #fff;
  border-color: #3f6772;
}
.year-select {
  margin-left: auto;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.3rem 0.55rem;
  font-size: 0.78rem;
  font-family: inherit;
}

/* Category accordion: clickable header that toggles a body of templates +
   rows + add-custom button. The chevron rotates on collapse. */
.category {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
}
.category-head {
  width: 100%;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  padding: 0.35rem 0.25rem;
  border-radius: 6px;
  transition: background 0.15s;
}
.category-head:hover { background: rgba(58,100,112,0.06); }
.cat-chevron {
  color: #3f6772;
  font-size: 0.8rem;
  transition: transform 0.2s;
  display: inline-block;
}
.category.collapsed .cat-chevron { transform: rotate(-90deg); }
.cat-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #34555f;
  flex-grow: 1;
}
.cat-progress {
  font-size: 0.74rem;
  color: #64748b;
  font-weight: 500;
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
}
.cat-progress.done {
  background: #d9e4e7;
  border-color: #a3bbc1;
  color: #244047;
}
.category-body {
  padding: 0.6rem 0.25rem 0.25rem;
  overflow-anchor: none;
}

/* Add-custom button + inline form (per category) */
.add-custom-btn {
  margin-top: 0.55rem;
  background: transparent;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #557d89;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.add-custom-btn:hover {
  border-color: #3f6772;
  background: #eef3f4;
  color: #34555f;
}
.add-custom-form {
  margin-top: 0.55rem;
  display: flex;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid #3f6772;
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  align-items: center;
}
.add-custom-form input {
  flex-grow: 1;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.35rem 0.55rem;
  font-size: 0.85rem;
  font-family: inherit;
  min-width: 0;
}
.add-custom-form input:focus {
  outline: none;
  border-color: #3f6772;
}
.btn-add,
.btn-cancel {
  font-family: inherit;
  font-size: 0.78rem;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
  flex-shrink: 0;
}
.btn-add {
  background: #3f6772;
  color: #fff;
  border-color: #3f6772;
}
.btn-add:hover { background: #34555f; border-color: #34555f; }
.btn-cancel {
  background: #fff;
  color: #475569;
  border-color: #cbd5e1;
}
.btn-cancel:hover { border-color: #94a3b8; }

.rotation-block {
  margin-top: 0.25rem;
  flex-shrink: 0;
  min-height: 0;
}

/* Hidden on screen; revealed only while printing (see unscoped print block below). */
.print-worksheet { display: none; }

/* Print: collapse the builder UI so only the .agreement-preview prints.
   We hide the banner, templates collapsible, editor column, and rotation
   table — leaving the preview to flow naturally on the printed page.
   The preview's own print rules (in AgreementPreview.vue) strip its border
   and padding so the prose fills the letter sheet cleanly. */
@media print {
  .holiday-builder { display: block; gap: 0; height: auto !important; max-height: none !important; }
  .holiday-builder,
  .holiday-builder * {
    overflow: visible !important;
    max-height: none !important;
  }
  .banner,
  .editor,
  .rotation-block,
  .preview-tabs { display: none !important; }
  .layout { display: block !important; gap: 0 !important; }
  .preview {
    position: static !important;
    max-height: none !important;
    overflow: visible !important;
    top: auto !important;
    display: block !important;
    gap: 0 !important;
  }
}
</style>

<!-- Unscoped print rules (PensionCalculator pattern). Gated on body.holiday-schedule-printing. -->
<style>
@media print {
  body.holiday-schedule-printing * { visibility: hidden !important; }
  body.holiday-schedule-printing .holiday-builder > *:not(.print-worksheet) {
    display: none !important;
  }
  body.holiday-schedule-printing .holiday-builder .print-worksheet,
  body.holiday-schedule-printing .holiday-builder .print-worksheet * {
    visibility: visible !important;
  }
  body.holiday-schedule-printing .holiday-builder .print-worksheet {
    display: block !important;
    position: static !important;
    width: 100% !important;
    box-sizing: border-box !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body.holiday-schedule-printing .holiday-builder .print-worksheet .preview-pane {
    display: block !important;
  }
  body.holiday-schedule-printing .holiday-builder .print-worksheet .preview-pane + .preview-pane {
    page-break-before: always;
    break-before: page;
  }

  /* Match the on-screen preview styling (orange title bar, intro callout, etc.). */
  body.holiday-schedule-printing .print-worksheet .agreement-preview {
    background: #fff !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    font-size: 0.8rem !important;
    line-height: 1.35 !important;
    color: #1e293b !important;
  }
  body.holiday-schedule-printing .print-worksheet .agreement-head h1 {
    margin: 0 0 0.25rem !important;
    font-size: 1.15rem !important;
    font-weight: 700 !important;
    color: #34555f !important;
    border-left: 3px solid #f7a91b !important;
    padding-left: 0.6rem !important;
  }
  body.holiday-schedule-printing .print-worksheet .agreement-parties {
    margin: 0 0 0.1rem !important;
    font-size: 0.85rem !important;
  }
  body.holiday-schedule-printing .print-worksheet .agreement-meta {
    margin: 0 0 0.55rem !important;
    font-size: 0.7rem !important;
    color: #64748b !important;
  }
  body.holiday-schedule-printing .print-worksheet .agreement-intro {
    margin: 0 0 0.7rem !important;
    font-size: 0.78rem !important;
    line-height: 1.35 !important;
    color: #475569 !important;
    border-left: 3px solid #a3bbc1 !important;
    padding: 0.3rem 0.7rem !important;
    background: #f8fafc !important;
    border-radius: 0 6px 6px 0 !important;
  }
  body.holiday-schedule-printing .print-worksheet .agreement-discuss-banner {
    margin: 0 0 0.7rem !important;
    font-size: 0.78rem !important;
    color: #85581a !important;
    border-left: 3px solid #f7a91b !important;
    padding: 0.35rem 0.7rem !important;
    background: #fef6e0 !important;
    border-radius: 0 6px 6px 0 !important;
  }
  body.holiday-schedule-printing .print-worksheet .agreement-section {
    margin-top: 0.7rem !important;
  }
  body.holiday-schedule-printing .print-worksheet .agreement-section h2 {
    margin: 0 0 0.35rem !important;
    font-size: 0.85rem !important;
    text-transform: uppercase !important;
    letter-spacing: 0.08em !important;
    color: #3f6772 !important;
    font-weight: 700 !important;
    border-bottom: 1px solid #e2e8f0 !important;
    padding-bottom: 0.25rem !important;
    page-break-after: avoid !important;
    break-after: avoid !important;
  }
  body.holiday-schedule-printing .print-worksheet .clauses {
    margin: 0 !important;
    padding-left: 1.2rem !important;
  }
  body.holiday-schedule-printing .print-worksheet .clauses li {
    margin-bottom: 0.25rem !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  @page { size: letter; margin: 0.6in; }
}
</style>
